import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn, formatCurrency } from "@/lib/utils";
import { PricingSkeleton } from "./pricing-skeleton";

import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiService } from "@/services/api-service";

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SubscriptionModal({ isOpen, onClose }: SubscriptionModalProps) {
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">(
    "monthly"
  );

  const subscribeMutation = useMutation({
    mutationFn: ApiService.subscribe,
    onSuccess: (data) => {
      if (data.url) {
        window.open(data.url, "_blank");
      }
    },
  });

  const handleSubscribe = (lookupKey: string) => {
    subscribeMutation.mutate(lookupKey);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const {
    data: products,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["products"],
    queryFn: ApiService.getProducts,
    enabled: !!isOpen,
  });
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-background rounded-xl shadow-xl w-full max-w-3xl overflow-hidden max-h-[80vh] overflow-y-auto cute-scrollbar"
        >
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-base font-semibold">Upgrade Your Plan</h2>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="p-6">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <PricingSkeleton className="mb-6" />
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center py-16">
                <AlertCircle className="mb-6 h-14 w-14 text-red-400" />
                <h3 className="text-lg font-medium mb-2">
                  Unable to fetch plans
                </h3>
                <p className=" text-sm text-center max-w-md text-red-400">
                  Something went wrong
                </p>
              </div>
            ) : (
              <>
                <div className="flex justify-center mb-8">
                  <div className="flex items-center space-x-2 bg-muted p-1 rounded-lg">
                    <span
                      className={cn(
                        "px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                        billingPeriod === "monthly"
                          ? "bg-background shadow-sm"
                          : "text-muted-foreground"
                      )}
                    >
                      Monthly
                    </span>
                    <Switch
                      checked={billingPeriod === "yearly"}
                      onCheckedChange={(checked) =>
                        setBillingPeriod(checked ? "yearly" : "monthly")
                      }
                    />
                    <span
                      className={cn(
                        "px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2",
                        billingPeriod === "yearly"
                          ? "bg-background shadow-sm"
                          : "text-muted-foreground"
                      )}
                    >
                      Yearly
                      {billingPeriod === "yearly" && (
                        <span className="bg-green-100 text-xs text-green-800 dark:bg-green-900 dark:text-green-100 px-2 py-0.5 rounded-full">
                          Save 10%
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                <div className="w-full flex items-center justify-center">
                  {products &&
                    products.map((product) => {
                      const price =
                        billingPeriod === "monthly"
                          ? product.prices.monthly
                          : product.prices.yearly;

                      return (
                        <motion.div
                          key={product.id}
                          whileHover={{ scale: 1.02 }}
                          className={cn(
                            "border rounded-xl p-6 cursor-pointer transition-colors sm:w-1/2",
                            selectedProductId === product.id
                              ? "border-secondary-100 bg-secondary-50 dark:"
                              : "hover:border-secondary-100/50"
                          )}
                          onClick={() => setSelectedProductId(product.id)}
                        >
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="text-sm font-semibold">
                                {product.name}
                              </h3>
                              <p className="text-neutral-600 text-xs">
                                {product.description}
                              </p>
                            </div>
                            <div
                              className={cn(
                                "w-10 h-6 rounded-full border flex items-center justify-center",
                                selectedProductId === product.id
                                  ? "bg-secondary-200 border-primary"
                                  : "border-muted-foreground"
                              )}
                            >
                              {selectedProductId === product.id && (
                                <Check className="h-3 w-3 text-neutral-900" />
                              )}
                            </div>
                          </div>

                          <div className="mb-6">
                            <div className="flex items-baseline">
                              <span className="text-lg font-bold">
                                {formatCurrency(price.amount)}
                              </span>
                              <span className="text-neutral-600 ml-1">
                                /
                                {billingPeriod === "monthly" ? "month" : "year"}
                              </span>
                            </div>
                            {billingPeriod === "yearly" && (
                              <p className="text-green-600 dark:text-green-400 text-sm mt-1">
                                Save 10% compared to monthly
                              </p>
                            )}
                          </div>

                          <ul className="space-y-2 mb-6">
                            {product.features.map((feature, index) => (
                              <li key={index} className="flex items-start">
                                <Check className="h-5 w-5 text-green-500 mr-2 shrink-0" />
                                <span className="text-xs">{feature}</span>
                              </li>
                            ))}
                          </ul>

                          <Button
                            className="w-full bg-secondary-700 hover:bg-secondary-800 cursor-pointer text-xs text-white hover:text-white"
                            onClick={() => handleSubscribe(price.lookup_key)}
                            disabled={subscribeMutation.isPending}
                            variant={
                              selectedProductId === product.id
                                ? "default"
                                : "outline"
                            }
                          >
                            {selectedProductId === product.id
                              ? "Subscribe Now"
                              : "Select Plan"}
                          </Button>
                        </motion.div>
                      );
                    })}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
